import { useState, useEffect } from 'react';

export const useFormState = (transaction, transactions, open, isBulkMode) => {
  const [entryType, setEntryType] = useState('regular'); // 'regular' or 'transfer'
  const [formData, setFormData] = useState({
    categoryId: '',
    description: '',
    individualDescriptions: {}, // Para múltiplas transações
    targetBankAccountId: '', // Para transferências
  });
  const [errors, setErrors] = useState({});

  // Determinar se estamos trabalhando com uma ou múltiplas transações
  const currentTransactions = isBulkMode ? transactions : transaction ? [transaction] : [];

  // Determinar o tipo das transações (sempre consistente no contexto do auto match)
  const transactionTypes = currentTransactions.map((txn) => txn.amount > 0);
  const allPositive = transactionTypes.every((type) => type === true); // Todas entrada
  const allNegative = transactionTypes.every((type) => type === false); // Todas saída

  // Preencher dados automaticamente quando as transações mudam
  useEffect(() => {
    if ((transaction || transactions) && open) {
      const txns = isBulkMode ? transactions : transaction ? [transaction] : [];

      // Para modo bulk, inicializar descrições individuais
      const individualDescriptions = {};
      if (isBulkMode) {
        txns.forEach((txn) => {
          individualDescriptions[txn.transactionHash] =
            txn.description || 'Lançamento criado a partir do extrato';
        });
      }

      setFormData({
        categoryId: '',
        description: isBulkMode
          ? ''
          : txns[0]?.description || 'Lançamento criado a partir do extrato',
        individualDescriptions,
        targetBankAccountId: '',
      });

      setEntryType('regular'); // Reset para tipo regular
      setErrors({});
    }
  }, [transaction, transactions, open, isBulkMode]);

  // Atualizar descrições individuais quando a descrição padrão muda (modo bulk)
  useEffect(() => {
    if (isBulkMode && currentTransactions.length > 0) {
      const updatedDescriptions = { ...formData.individualDescriptions };
      let hasChanges = false;

      currentTransactions.forEach((txn) => {
        if (formData.description && formData.description.trim()) {
          // Se há descrição padrão preenchida, substituir TODAS as descrições individuais
          updatedDescriptions[txn.transactionHash] = formData.description;
          hasChanges = true;
        } else {
          // Se a descrição padrão foi limpa, restaurar a descrição original da transação
          const originalDesc = txn.description || 'Lançamento criado a partir do extrato';
          if (updatedDescriptions[txn.transactionHash] !== originalDesc) {
            updatedDescriptions[txn.transactionHash] = originalDesc;
            hasChanges = true;
          }
        }
      });

      if (hasChanges) {
        setFormData((prev) => ({
          ...prev,
          individualDescriptions: updatedDescriptions,
        }));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.description, isBulkMode, currentTransactions]);

  const handleInputChange = (field, value, transactionHash = null) => {
    if (field === 'individualDescription' && transactionHash) {
      setFormData((prev) => ({
        ...prev,
        individualDescriptions: {
          ...prev.individualDescriptions,
          [transactionHash]: value,
        },
      }));

      // Limpar erro do campo individual quando usuário começa a digitar
      if (errors[`individual_${transactionHash}`]) {
        setErrors((prev) => ({
          ...prev,
          [`individual_${transactionHash}`]: '',
        }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));

      // Limpar erro do campo quando usuário começa a digitar
      if (errors[field]) {
        setErrors((prev) => ({
          ...prev,
          [field]: '',
        }));
      }
    }
  };

  // Resetar campos quando o tipo de lançamento mudar
  useEffect(() => {
    if (entryType === 'transfer') {
      // Ao mudar para transferência, limpar categoria
      setFormData((prev) => ({
        ...prev,
        categoryId: '',
      }));
    } else {
      // Ao mudar para regular, limpar conta bancária de destino
      setFormData((prev) => ({
        ...prev,
        targetBankAccountId: '',
      }));
    }
  }, [entryType]);

  return {
    entryType,
    setEntryType,
    formData,
    setFormData,
    errors,
    setErrors,
    handleInputChange,
    allPositive,
    allNegative,
    currentTransactions,
  };
};
